import { createPortal } from 'react-dom';
import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, ArrowRight, Settings2 } from 'lucide-react';
import type { SessionConfig } from '../types';
import { Logo } from './Logo';
import { getPersonalGoogleConnectUrl } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import { scopedStorageKey } from '../store/userStore';

const GOOGLE_ONBOARDING_KEY_PREFIX = 'octopm-google-onboarding';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  sessionConfig: SessionConfig | null;
  /** Open directly to step 2 (Google connect), e.g. returning from OAuth */
  initialStep?: number;
  /** Show Google as already connected, e.g. returning after successful OAuth */
  initialGoogleConnected?: boolean;
}

const FEATURES = [
  {
    icon: '🔌',
    title: 'Connect Everything',
    desc: 'Ingest signals from Jira, Google, and Slack for real-time visibility.',
  },
  {
    icon: '🛡️',
    title: 'Assess Risk',
    desc: 'AI monitors velocity and dependencies to predict blockers early.',
  },
  {
    icon: '📢',
    title: 'Update Stakeholders',
    desc: 'Automate role-specific status reports to keep everyone aligned.',
  },
  {
    icon: '✨',
    title: 'AI Powered',
    desc: 'Our advanced AI synthesizes complex data into actionable decisions.',
  },
];

function StepDots({ total, current }: { total: number; current: number }) {
  return (
    <div className="flex items-center justify-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`rounded-full transition-all duration-200 ${
            i === current
              ? 'w-4 h-1.5 bg-primary'
              : 'w-1.5 h-1.5 bg-gray-200 dark:bg-white/20'
          }`}
        />
      ))}
    </div>
  );
}

export function WelcomeModal({
  isOpen,
  onClose,
  onComplete,
  sessionConfig,
  initialStep = 0,
  initialGoogleConnected = false,
}: WelcomeModalProps) {
  const [step, setStep] = useState(initialStep);
  const [googleConnected, setGoogleConnected] = useState(initialGoogleConnected);
  const [connectError, setConnectError] = useState<string | null>(null);

  // Sync when parent updates initial values (e.g. returning from OAuth with google_connected=1)
  useEffect(() => { setStep(initialStep); }, [initialStep]);
  useEffect(() => { setGoogleConnected(initialGoogleConnected); }, [initialGoogleConnected]);
  const qc = useQueryClient();

  const connectMutation = useMutation({
    mutationFn: getPersonalGoogleConnectUrl,
    onSuccess: ({ url }) => {
      // Flag so OAuthCallback knows to return to the onboarding flow.
      // Scoped to entityId:userId — same reasoning as activeRole's storage
      // key (see userStore.ts): a global key here would let one account's
      // pending onboarding redirect fire for a different account/tenant
      // that happens to complete an OAuth callback in the same browser
      // profile before this one does.
      const key = scopedStorageKey(GOOGLE_ONBOARDING_KEY_PREFIX, sessionConfig?.entityId, sessionConfig?.userId);
      if (key) {
        try {
          localStorage.setItem(key, '1');
        } catch {
          // ignore storage errors
        }
      }
      window.location.href = url;
    },
    onError: (err: Error) => {
      setConnectError(err.message || 'Could not start Google authorization.');
    },
  });

  const handleComplete = () => {
    void qc.invalidateQueries({ queryKey: queryKeys.personalIntegrations });
    onComplete();
    setStep(0);
    onClose();
  };

  const handleSkip = () => {
    onComplete();
    setStep(0);
    onClose();
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleSkip}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', duration: 0.4 }}
            className="relative w-full max-w-2xl bg-white dark:bg-bg-surface text-gray-900 dark:text-white rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Primary banner */}
            <div className="bg-primary h-28 relative overflow-hidden flex items-center justify-center">
              <div className="absolute inset-0 bg-[url('/background.svg')] opacity-30 bg-cover bg-center pointer-events-none" />
              <button
                onClick={handleSkip}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="relative z-10 flex flex-col items-center">
                <Logo monochrome className="h-12 w-auto text-white" />
              </div>
            </div>

            {/* Content */}
            <div className="p-8">
              <AnimatePresence mode="wait">
                {step === 0 && (
                  <motion.div
                    key="step-0"
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <h2 className="text-2xl font-bold font-display">Unified Project Intelligence</h2>
                      <span className="text-2xl">⚡</span>
                    </div>

                    {sessionConfig && (
                      <p className="text-meta text-gray-500 dark:text-gray-400 -mt-2 mb-4">
                        {sessionConfig.entityName} ·{' '}
                        <span className="capitalize">
                          {sessionConfig.functionalRoles?.length
                            ? sessionConfig.functionalRoles.join(', ').replace(/_/g, ' ')
                            : sessionConfig.role?.replace(/_/g, ' ') ?? ''}
                        </span>
                      </p>
                    )}

                    <h3 className="text-title font-semibold mb-2">We built OctoPM for you</h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-6 text-meta leading-relaxed">
                      With over 30 years of product leadership experience, we built OctoPM to solve
                      the fragmentation of modern product management. Instead of adding another tool
                      to your stack, we connect your existing tools to provide a single, real-time
                      view of progress, risk, and alignment.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
                      {FEATURES.map((f) => (
                        <div
                          key={f.title}
                          className="p-4 rounded-lg bg-gray-50 dark:bg-white/5 border border-transparent hover:border-primary/20 transition-colors"
                        >
                          <h4 className="font-bold text-meta mb-1">
                            {f.icon} {f.title}
                          </h4>
                          <p className="text-micro text-gray-500 dark:text-gray-400">{f.desc}</p>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center justify-between">
                      <StepDots total={2} current={0} />
                      <motion.button
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        onClick={() => setStep(1)}
                        className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-6 py-2.5 rounded-full font-medium transition-colors shadow-lg shadow-primary/20 text-meta"
                      >
                        Next
                        <ArrowRight className="h-4 w-4" />
                      </motion.button>
                    </div>
                  </motion.div>
                )}

                {step === 1 && (
                  <motion.div
                    key="step-1"
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.2 }}
                  >
                    <h2 className="text-title font-bold font-display mb-1">Connect Google Workspace</h2>
                    <p className="text-meta text-gray-500 dark:text-gray-400 mb-6">
                      Connect your personal Google account so OctoPM can pull signals from your Drive, Docs,
                      Sheets, Slides, Calendar, and Meet — giving you richer context on every project.
                    </p>

                    <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 p-5 mb-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="h-10 w-10 rounded-xl bg-white dark:bg-white/10 flex items-center justify-center border border-gray-100 dark:border-transparent">
                          <img src="/integrations/google.svg" alt="Google" className="h-6 w-6" />
                        </div>
                        <div>
                          <p className="text-meta font-semibold text-gray-900 dark:text-white">Google Workspace</p>
                          <p className="text-micro text-gray-500 dark:text-gray-400">Drive · Docs · Sheets · Slides · Calendar · Meet</p>
                        </div>
                        {googleConnected && (
                          <span className="ml-auto flex items-center gap-1 text-micro text-green-600 dark:text-green-400">
                            <CheckCircle2 className="h-4 w-4" />
                            Connected
                          </span>
                        )}
                      </div>

                      <ul className="space-y-1.5 mb-5">
                        {[
                          'Read files and folders from your Drive',
                          'Import Docs, Sheets, and Slides linked to projects',
                          'Read Calendar events and meeting notes',
                          'Ingest Meet recordings and transcripts',
                          'Write status updates back to Sheets or Docs',
                        ].map((item) => (
                          <li key={item} className="flex items-start gap-2 text-micro text-gray-600 dark:text-gray-300">
                            <span className="mt-0.5 text-primary">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>

                      {connectError && (
                        <p className="text-micro text-red-400 mb-3">{connectError}</p>
                      )}

                      {!googleConnected && (
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => { setConnectError(null); connectMutation.mutate(); }}
                          disabled={connectMutation.isPending}
                          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 hover:bg-gray-50 dark:hover:bg-white/10 text-meta font-medium text-gray-900 dark:text-white transition-colors disabled:opacity-50"
                        >
                          {connectMutation.isPending ? (
                            <><div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />Connecting…</>
                          ) : (
                            <><img src="/integrations/google.svg" alt="" className="h-4 w-4" />Authorize Google Workspace</>
                          )}
                        </motion.button>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <StepDots total={2} current={1} />
                      <div className="flex items-center gap-2">
                        {!googleConnected && (
                          <div className="text-right">
                            <button
                              onClick={handleSkip}
                              className="text-meta text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors px-4 py-2.5"
                            >
                              Skip for now
                            </button>
                          </div>
                        )}
                        {googleConnected && (
                          <motion.button
                            whileHover={{ scale: 1.04 }}
                            whileTap={{ scale: 0.96 }}
                            onClick={handleComplete}
                            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-6 py-2.5 rounded-full font-medium transition-colors shadow-lg shadow-primary/20 text-meta"
                          >
                            Get started
                            <ArrowRight className="h-4 w-4" />
                          </motion.button>
                        )}
                      </div>
                    </div>

                    {!googleConnected && (
                      <p className="mt-4 flex items-center gap-1.5 text-micro text-gray-400 dark:text-gray-500">
                        <Settings2 className="h-4 w-4 flex-shrink-0" />
                        You can connect later in{' '}
                        <span className="font-medium text-gray-500 dark:text-gray-400">Settings → Integrations</span>
                      </p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
